/**
 * RTWO Indexer Engine — Internal Event Bus
 */

export const IndexerInternalEventTypes = {
  JOB_CREATED: 'job_created',
  BLOCK_INDEXED: 'block_indexed',
  TRANSACTION_INDEXED: 'transaction_indexed',
  ADDRESS_INDEXED: 'address_indexed',
  ASSET_INDEXED: 'asset_indexed',
  EVENT_INDEXED: 'event_indexed',
  QUERY_EXECUTED: 'query_executed',
  SEARCH_EXECUTED: 'search_executed',
  STORAGE_BINDING_CHANGED: 'storage_binding_changed',
} as const;

export type IndexerInternalEventType =
  (typeof IndexerInternalEventTypes)[keyof typeof IndexerInternalEventTypes];

export interface IndexerInternalEvent<T = unknown> {
  id: string;
  type: IndexerInternalEventType;
  moduleId: 'RTWO-IDXR-001';
  payload: T;
  timestamp: number | null;
}

export type IndexerInternalEventHandler<T = unknown> = (event: IndexerInternalEvent<T>) => void;

class IndexerInternalEventBus {
  private subscriptions = new Map<IndexerInternalEventType, IndexerInternalEventHandler[]>();

  subscribe<T>(eventType: IndexerInternalEventType, handler: IndexerInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    handlers.push(handler as IndexerInternalEventHandler);
    this.subscriptions.set(eventType, handlers);
  }

  unsubscribe<T>(
    eventType: IndexerInternalEventType,
    handler: IndexerInternalEventHandler<T>
  ): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    this.subscriptions.set(
      eventType,
      handlers.filter((entry) => entry !== handler)
    );
  }

  emit<T>(eventType: IndexerInternalEventType, payload: T): string {
    const event: IndexerInternalEvent<T> = {
      id: `idxr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: eventType,
      moduleId: 'RTWO-IDXR-001',
      payload,
      timestamp: null,
    };

    const handlers = this.subscriptions.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[IndexerInternalEventBus] Handler error for ${eventType}:`, error);
      }
    }

    return event.id;
  }
}

export const indexerInternalEventBus = new IndexerInternalEventBus();
